import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Loader2, X, Plus, Tag, Code2, AlignLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/hooks/useAdminAuth';

interface Category { id: string; name: string; slug: string; }
interface TagItem { id: string; name: string; slug: string; }

const AdminBlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const { toast } = useToast();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [allTags, setAllTags] = useState<TagItem[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [newTagName, setNewTagName] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('html');
  const docViewRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    title: '', slug: '', excerpt: '', content: '', featured_image: '', category_id: '', status: 'draft',
  });

  useEffect(() => { fetchCategories(); fetchTags(); if (!isNew && id) fetchPost(id); }, [id, isNew]);

  const fetchCategories = async () => {
    const { data } = await supabase.from('blog_categories').select('id, name, slug').order('name');
    setCategories(data || []);
  };

  const fetchTags = async () => {
    const { data } = await supabase.from('blog_tags').select('id, name, slug').order('name');
    setAllTags(data || []);
  };

  const fetchPost = async (postId: string) => {
    try {
      const [{ data, error }, { data: postTagsData }] = await Promise.all([
        supabase.from('blog_posts').select('*').eq('id', postId).maybeSingle(),
        supabase.from('blog_post_tags').select('tag_id').eq('post_id', postId),
      ]);
      if (error) throw error;
      if (!data) { toast({ title: 'Not found', variant: 'destructive' }); navigate('/admin/blog'); return; }
      setFormData({
        title: data.title, slug: data.slug, excerpt: data.excerpt || '', content: data.content,
        featured_image: data.featured_image || '', category_id: data.category_id || '', status: data.status,
      });
      if (postTagsData) setSelectedTagIds(postTagsData.map((pt: any) => pt.tag_id));
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (activeTab === 'text' && docViewRef.current) { docViewRef.current.innerHTML = formData.content; }
  }, [activeTab]);

  const generateSlug = (title: string) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleTitleChange = (title: string) => {
    setFormData(prev => ({ ...prev, title, slug: isNew ? generateSlug(title) : prev.slug }));
  };

  const handleTagToggle = (tagId: string) => {
    setSelectedTagIds(prev => prev.includes(tagId) ? prev.filter(i => i !== tagId) : [...prev, tagId]);
  };

  const handleCreateTag = async () => {
    const name = newTagName.trim();
    if (!name) return;
    const slug = generateSlug(name);
    const { data, error } = await supabase.from('blog_tags').insert({ name, slug }).select().single();
    if (error) { toast({ title: 'Error', description: 'Tag already exists or failed to create', variant: 'destructive' }); return; }
    setAllTags(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
    setSelectedTagIds(prev => [...prev, data.id]);
    setNewTagName('');
  };

  const handleSave = async (publish = false) => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast({ title: 'Validation Error', description: 'Title and content are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const postData = {
        title: formData.title.trim(), slug: formData.slug || generateSlug(formData.title),
        excerpt: formData.excerpt.trim() || null, content: formData.content,
        featured_image: formData.featured_image.trim() || null,
        category_id: formData.category_id || null,
        status: publish ? 'published' : formData.status,
        published_at: publish ? new Date().toISOString() : null,
        author_id: user?.id,
      };
      let postId = id;
      if (isNew) {
        const { data, error } = await supabase.from('blog_posts').insert(postData).select().single();
        if (error) throw error;
        postId = data.id;
      } else {
        const { error } = await supabase.from('blog_posts').update(postData).eq('id', id!);
        if (error) throw error;
        await supabase.from('blog_post_tags').delete().eq('post_id', id!);
      }
      if (selectedTagIds.length > 0 && postId) {
        await supabase.from('blog_post_tags').insert(selectedTagIds.map(tag_id => ({ post_id: postId!, tag_id })));
      }
      toast({ title: 'Success', description: `Post ${isNew ? 'created' : 'updated'} successfully` });
      navigate('/admin/blog');
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <AdminLayout><div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-6xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/blog')}><ArrowLeft className="h-4 w-4" /></Button>
            <h1 className="text-3xl font-bold text-foreground">{isNew ? 'New Blog Post' : 'Edit Blog Post'}</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPreview(!showPreview)}><Eye className="mr-2 h-4 w-4" />{showPreview ? 'Close Preview' : 'Preview'}</Button>
            <Button variant="outline" onClick={() => handleSave(false)} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Draft</Button>
            <Button onClick={() => handleSave(true)} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Publish</Button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
          <div className="space-y-6">
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="space-y-2"><Label htmlFor="title">Title</Label><Input id="title" value={formData.title} onChange={(e) => handleTitleChange(e.target.value)} placeholder="Enter post title" /></div>
                <div className="space-y-2"><Label htmlFor="slug">Slug</Label><Input id="slug" value={formData.slug} onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))} placeholder="post-url-slug" /></div>
                <div className="space-y-2"><Label htmlFor="excerpt">Excerpt</Label><Textarea id="excerpt" value={formData.excerpt} onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))} placeholder="Brief description for previews..." rows={3} /></div>
                <div className="space-y-2">
                  <Label>Content</Label>
                  <Tabs defaultValue="html" className="w-full" onValueChange={setActiveTab}>
                    <TabsList className="mb-2">
                      <TabsTrigger value="html" className="flex items-center gap-1.5"><Code2 className="h-3.5 w-3.5" />HTML</TabsTrigger>
                      <TabsTrigger value="text" className="flex items-center gap-1.5"><AlignLeft className="h-3.5 w-3.5" />Document View</TabsTrigger>
                    </TabsList>
                    <TabsContent value="html">
                      <Textarea id="content" value={formData.content} onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))} placeholder="<p>Write your blog post content in HTML...</p>" rows={20} className="font-mono text-sm" />
                    </TabsContent>
                    <TabsContent value="text">
                      <div className="rounded-lg border border-input bg-white shadow-sm overflow-auto min-h-[480px]">
                        <div ref={docViewRef} contentEditable suppressContentEditableWarning onInput={(e) => setFormData(prev => ({ ...prev, content: (e.currentTarget as HTMLDivElement).innerHTML }))} className="min-h-[480px] w-full px-6 py-6 focus-visible:outline-none prose prose-lg max-w-none [&>*]:max-w-none" />
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </CardContent>
            </Card>
            {showPreview && <Card><CardHeader><CardTitle>Preview</CardTitle></CardHeader><CardContent><div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: formData.content }} /></CardContent></Card>}
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader><CardTitle>Settings</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Published</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={formData.category_id} onValueChange={(v) => setFormData(prev => ({ ...prev, category_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>{categories.map((cat) => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Featured Image URL</Label><Input value={formData.featured_image} onChange={(e) => setFormData(prev => ({ ...prev, featured_image: e.target.value }))} placeholder="https://..." /></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Tag className="h-4 w-4" />Tags</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {selectedTagIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedTagIds.map(tagId => {
                      const tag = allTags.find(t => t.id === tagId);
                      if (!tag) return null;
                      return <Badge key={tagId} variant="secondary" className="flex items-center gap-1 pr-1">{tag.name}<button onClick={() => handleTagToggle(tagId)} className="ml-0.5 rounded-full hover:bg-muted p-0.5"><X className="h-3 w-3" /></button></Badge>;
                    })}
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {allTags.filter(t => !selectedTagIds.includes(t.id)).map(tag => (
                    <button key={tag.id} onClick={() => handleTagToggle(tag.id)} className="text-xs px-2 py-1 rounded-full border border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors">+ {tag.name}</button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={newTagName} onChange={(e) => setNewTagName(e.target.value)} placeholder="New tag..." className="text-sm" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleCreateTag())} />
                  <Button size="sm" variant="outline" onClick={handleCreateTag}><Plus className="h-3 w-3" /></Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminBlogEditor;
